import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { apiClient } from '../utils/api';
import { createAct, CreateActData } from '../services';
import { useToast } from '../components/Toast';
import { DateTimeInput } from '../components/DateTimeInput';

type Option = { id: string; name: string };

const formatLocalDateTime = (value: string) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

export function ActCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, ToastComponent } = useToast();

  const [formData, setFormData] = useState<CreateActData>({
    appointmentId: undefined,
    serviceId: '',
    practitionerId: '',
    shopId: '',
    clientId: undefined,
    status: 'PLANNED',
    notes: '',
    performedAt: undefined,
  });

  const [performedAtInput, setPerformedAtInput] = useState('');
  const [plannedStartAtInput, setPlannedStartAtInput] = useState('');
  const [plannedEndAtInput, setPlannedEndAtInput] = useState('');
  const [shops, setShops] = useState<Option[]>([]);
  const [practitioners, setPractitioners] = useState<Option[]>([]);
  const [services, setServices] = useState<Option[]>([]);
  const [clients, setClients] = useState<Option[]>([]);
  const [appointments, setAppointments] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [initialParams, setInitialParams] = useState<Partial<CreateActData>>({});
  const [hasAppliedParams, setHasAppliedParams] = useState(false);

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const appointmentId = params.get('appointmentId') || undefined;
    const shopId = params.get('shopId') || undefined;
    const practitionerId = params.get('practitionerId') || undefined;
    const clientId = params.get('clientId') || undefined;
    const serviceId = params.get('serviceId') || undefined;

    setInitialParams({
      appointmentId,
      shopId,
      practitionerId,
      clientId,
      serviceId,
    });
  }, [location.search]);

  useEffect(() => {
    if (loading || hasAppliedParams) return;
    if (Object.keys(initialParams).length === 0) return;
    setFormData((prev) => ({
      ...prev,
      ...initialParams,
    }));
    setHasAppliedParams(true);
  }, [hasAppliedParams, initialParams, loading]);

  const loadOptions = async () => {
    try {
      setLoading(true);
      const [shopsRes, practitionersRes, servicesRes, clientsRes, appointmentsRes] = await Promise.all([
        apiClient('/api/admin/shops?limit=1000'),
        apiClient('/api/admin/practitioners?limit=1000'),
        apiClient('/api/admin/services?limit=1000'),
        apiClient('/api/admin/clients?limit=1000'),
        apiClient('/api/admin/appointments?limit=500'),
      ]);

      const [shopsData, practitionersData, servicesData, clientsData, appointmentsData] = await Promise.all([
        shopsRes.json(),
        practitionersRes.json(),
        servicesRes.json(),
        clientsRes.json(),
        appointmentsRes.json(),
      ]);

      if (shopsData.success) {
        setShops(
          (shopsData.data?.shops || []).map((shop: any) => ({
            id: shop.id,
            name: shop.name,
          }))
        );
      }

      if (practitionersData.success) {
        setPractitioners(
          (practitionersData.data?.practitioners || []).map((practitioner: any) => ({
            id: practitioner.id,
            name: `${practitioner.firstName || ''} ${practitioner.lastName || ''}`.trim() || practitioner.email,
          }))
        );
      }

      if (servicesData.success) {
        setServices(
          (servicesData.data?.services || []).map((service: any) => ({
            id: service.id,
            name: service.name,
          }))
        );
      }

      if (clientsData.success) {
        setClients(
          (clientsData.data?.clients || []).map((client: any) => ({
            id: client.id,
            name: `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.email,
          }))
        );
      }

      if (appointmentsData.success) {
        setAppointments(
          (appointmentsData.data?.appointments || []).map((appointment: any) => {
            const clientName = `${appointment.clientFirstName || ''} ${appointment.clientLastName || ''}`.trim()
              || appointment.clientEmail
              || 'Client inconnu';
            const date = appointment.scheduledAt
              ? new Date(appointment.scheduledAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : '';
            return {
              id: appointment.id,
              name: date ? `${clientName} — ${date}` : clientName,
            };
          })
        );
      }

    } catch (err) {
      console.error('Failed to load act options:', err);
      setError('Impossible de charger les données du formulaire');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateActData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.shopId || !formData.practitionerId || !formData.serviceId) {
      setError('Veuillez renseigner la boutique, le praticien et le service');
      showToast('Veuillez remplir les champs obligatoires', 'warning');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);

      await createAct({
        ...formData,
        appointmentId: formData.appointmentId || undefined,
        clientId: formData.clientId || undefined,
        notes: formData.notes || undefined,
        sequence: formData.sequence || undefined,
        durationMinutes: formData.durationMinutes || undefined,
        plannedStartAt: plannedStartAtInput ? formatLocalDateTime(plannedStartAtInput) : undefined,
        plannedEndAt: plannedEndAtInput ? formatLocalDateTime(plannedEndAtInput) : undefined,
        performedAt: performedAtInput ? formatLocalDateTime(performedAtInput) : undefined,
      });

      showToast('Acte créé avec succès', 'success');
      navigate('/planning/acts');
    } catch (err: any) {
      console.error('Create act error:', err);
      const msg = err.message || 'Erreur de connexion au serveur';
      setError(msg);
      setErrorDetails(Array.isArray(err.errors) ? err.errors : []);
      showToast(msg, 'error', 7000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/planning/acts')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à la liste</span>
          </button>
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">Créer un acte</h1>
          </div>
        </div>
        <button
          type="submit"
          form="act-create-form"
          disabled={saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Création...' : "Créer l'acte"}</span>
        </button>
      </div>

      <ToastComponent />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <form id="act-create-form" onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <h2 className="text-xl font-semibold text-textPrimary mb-6">Informations principales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Boutique *</label>
              <select
                value={formData.shopId}
                onChange={(e) => handleInputChange('shopId', e.target.value)}
                className="input-field"
                required
              >
                <option value="">Sélectionner une boutique</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Praticien *</label>
              <select
                value={formData.practitionerId}
                onChange={(e) => handleInputChange('practitionerId', e.target.value)}
                className="input-field"
                required
              >
                <option value="">Sélectionner un praticien</option>
                {practitioners.map((practitioner) => (
                  <option key={practitioner.id} value={practitioner.id}>
                    {practitioner.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Service *</label>
              <select
                value={formData.serviceId}
                onChange={(e) => handleInputChange('serviceId', e.target.value)}
                className="input-field"
                required
              >
                <option value="">Sélectionner un service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Client</label>
              <select
                value={formData.clientId || ''}
                onChange={(e) => handleInputChange('clientId', e.target.value || undefined)}
                className="input-field"
              >
                <option value="">Aucun client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Rendez-vous</label>
              <select
                value={formData.appointmentId || ''}
                onChange={(e) => handleInputChange('appointmentId', e.target.value || undefined)}
                className="input-field"
              >
                <option value="">Aucun rendez-vous</option>
                {appointments.map((appointment) => (
                  <option key={appointment.id} value={appointment.id}>
                    {appointment.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Statut</label>
              <select
                value={formData.status || 'PLANNED'}
                onChange={(e) => handleInputChange('status', e.target.value as CreateActData['status'])}
                className="input-field"
              >
                <option value="PLANNED">Planifié</option>
                <option value="IN_PROGRESS">En cours</option>
                <option value="COMPLETED">Terminé</option>
                <option value="CANCELLED">Annulé</option>
              </select>
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-textPrimary mb-2">Début planifié</label>
               <DateTimeInput
                    value={plannedStartAtInput}
                    onChange={setPlannedStartAtInput}
                    className="input-field"
                  />
                  </div>

                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Fin planifiée
                    </label>

                    <DateTimeInput
                      value={plannedEndAtInput}
                      onChange={setPlannedEndAtInput}
                      className="input-field"
                    />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Séquence</label>
              <input
                type="number"
                min={1}
                value={formData.sequence || ''}
                onChange={(e) => handleInputChange('sequence', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                className="input-field"
                placeholder="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Durée (minutes)</label>
              <input
                type="number"
                min={1}
                value={formData.durationMinutes || ''}
                onChange={(e) =>
                  handleInputChange('durationMinutes', e.target.value ? parseInt(e.target.value, 10) : undefined)
                }
                className="input-field"
                placeholder="30"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-textPrimary mb-2">Réalisé le</label>
              <DateTimeInput
                value={performedAtInput}
                onChange={setPerformedAtInput}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-textPrimary mb-2">Notes</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="input-field"
                placeholder="Notes internes sur l'acte..."
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
